// src/components/subscription/CheckoutModal.jsx
import React, { useState } from 'react';
import { useSubscription } from '../../context/SubscriptionContext';
import { 
  XMarkIcon, 
  ShieldCheckIcon, 
  CreditCardIcon, 
  QrCodeIcon, 
  BuildingLibraryIcon, 
  CheckCircleIcon,
  SparklesIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

export default function CheckoutModal() {
  const { isCheckoutOpen, closeCheckout, selectedPlanForCheckout, completePayment } = useSubscription();

  const [paymentMethod, setPaymentMethod] = useState('upi'); // 'upi', 'card', 'netbanking'
  const [upiId, setUpiId] = useState('user@okaxis');
  const [cardNumber, setCardNumber] = useState('4242 •••• •••• 4242');
  const [cardExpiry, setCardExpiry] = useState('12/28');
  const [cardCvv, setCardCvv] = useState('888');
  const [selectedBank, setSelectedBank] = useState('HDFC Bank');
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState('input'); // 'input', 'verifying', 'success'

  if (!isCheckoutOpen || !selectedPlanForCheckout) return null;

  const plan = selectedPlanForCheckout;
  const isAnnual = plan.billingCycle === 'yearly';
  const price = isAnnual ? plan.annual_price || (plan.price * 10) : plan.price;
  const orderId = `order_sim_${Date.now().toString().slice(-6)}`;

  const handlePay = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setStep('verifying');

    // Simulate real gateway roundtrip (1.2s realistic gateway experience)
    setTimeout(async () => {
      const paymentPayload = {
        plan_id: plan.id,
        billing_cycle: plan.billingCycle || 'monthly',
        order_id: orderId,
        payment_method: paymentMethod === 'upi' 
          ? `UPI (${upiId})` 
          : paymentMethod === 'card' 
            ? `Card (ending in 4242)` 
            : `Netbanking (${selectedBank})`
      };

      const result = await completePayment(paymentPayload);
      setProcessing(false);
      if (result.success) {
        setStep('success');
      } else {
        setStep('input');
      }
    }, 1200);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden transform transition-all">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1B2A41] to-[#2E4057] text-white p-6 relative">
          <button 
            onClick={closeCheckout} 
            disabled={processing}
            className="absolute top-5 right-5 text-gray-300 hover:text-white transition disabled:opacity-50"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-2 mb-1">
            <span className="bg-[#E8612C] text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
              Secure Checkout
            </span>
            <span className="text-xs text-gray-300 font-mono">
              #{orderId}
            </span>
          </div>

          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            Upgrade to {plan.name} Plan
          </h2>
          <p className="text-xs text-gray-300 mt-1">
            {plan.tagline || 'Activate full system access with instant deployment.'}
          </p>
        </div>

        {/* Order Summary Strip */}
        <div className="bg-[#F8FAFC] border-b border-gray-200 px-6 py-3 flex justify-between items-center text-sm">
          <div>
            <span className="font-semibold text-gray-800">{plan.name} Tier</span>
            <span className="text-xs text-gray-500 ml-2">
              ({isAnnual ? 'Billed Annually' : 'Billed Monthly'})
            </span>
          </div>
          <div className="text-right">
            <div className="text-lg font-black text-[#1B2A41]">
              ₹{Number(price).toLocaleString('en-IN')}
            </div>
            <div className="text-[10px] text-emerald-600 font-medium">
              Tax included • Instant Activation
            </div>
          </div>
        </div>

        {step === 'verifying' ? (
          <div className="p-10 text-center space-y-4">
            <ArrowPathIcon className="w-12 h-12 text-[#E8612C] animate-spin mx-auto" />
            <h3 className="text-base font-bold text-gray-800">Processing Payment...</h3>
            <p className="text-xs text-gray-500 max-w-xs mx-auto">
              Securely contacting payment gateway and generating subscription credentials. Do not refresh.
            </p>
          </div>
        ) : (
          <form onSubmit={handlePay} className="p-6 space-y-5">
            
            {/* Payment Method Selector Tabs */}
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                Select Payment Mode
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('upi')}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-semibold transition ${
                    paymentMethod === 'upi'
                      ? 'border-[#E8612C] bg-[#FFF5F0] text-[#E8612C] shadow-sm'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <QrCodeIcon className="w-5 h-5 mb-1" />
                  UPI / QR
                </button>

                <button
                  type="button"
                  onClick={() => setPaymentMethod('card')}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-semibold transition ${
                    paymentMethod === 'card'
                      ? 'border-[#E8612C] bg-[#FFF5F0] text-[#E8612C] shadow-sm'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <CreditCardIcon className="w-5 h-5 mb-1" />
                  Cards
                </button>

                <button
                  type="button"
                  onClick={() => setPaymentMethod('netbanking')}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-semibold transition ${
                    paymentMethod === 'netbanking'
                      ? 'border-[#E8612C] bg-[#FFF5F0] text-[#E8612C] shadow-sm'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <BuildingLibraryIcon className="w-5 h-5 mb-1" />
                  Netbanking
                </button>
              </div>
            </div>

            {/* Method Inputs */}
            {paymentMethod === 'upi' && (
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl space-y-3">
                <label className="block text-xs font-semibold text-gray-700">
                  Virtual Payment Address (VPA / UPI ID)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    required
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    placeholder="username@okhdfcbank"
                    className="flex-1 bg-white border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8612C]"
                  />
                  <button
                    type="button"
                    onClick={() => setUpiId('demo@upi')}
                    className="text-xs text-[#E8612C] font-semibold hover:underline px-2"
                  >
                    Demo UPI
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 flex items-center gap-1">
                  <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                  Supports Google Pay, PhonePe, Paytm, and BHIM
                </p>
              </div>
            )}

            {paymentMethod === 'card' && (
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Card Number
                  </label>
                  <input
                    type="text"
                    required
                    value={cardNumber}
                    onChange={(e) => setCardNumber(e.target.value)}
                    placeholder="4242 4242 4242 4242"
                    className="w-full bg-white border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8612C]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Expiry</label>
                    <input
                      type="text"
                      required
                      value={cardExpiry}
                      onChange={(e) => setCardExpiry(e.target.value)}
                      placeholder="MM/YY"
                      className="w-full bg-white border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8612C]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">CVV / CVC</label>
                    <input
                      type="password"
                      maxLength={4}
                      required
                      value={cardCvv}
                      onChange={(e) => setCardCvv(e.target.value)}
                      placeholder="123"
                      className="w-full bg-white border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8612C]"
                    />
                  </div>
                </div>
              </div>
            )}

            {paymentMethod === 'netbanking' && (
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl space-y-3">
                <label className="block text-xs font-semibold text-gray-700">
                  Select Popular Bank
                </label>
                <select
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                  className="w-full bg-white border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8612C]"
                >
                  <option value="HDFC Bank">HDFC Bank</option>
                  <option value="State Bank of India">State Bank of India (SBI)</option>
                  <option value="ICICI Bank">ICICI Bank</option>
                  <option value="Axis Bank">Axis Bank</option>
                  <option value="Kotak Mahindra Bank">Kotak Mahindra Bank</option>
                </select>
              </div>
            )}

            {/* Gateway Mode Badge */}
            <div className="flex items-center justify-between text-xs text-gray-500 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
              <span className="flex items-center gap-1.5 text-amber-800 font-medium">
                <SparklesIcon className="w-4 h-4 text-amber-600" />
                Gateway Simulation Mode
              </span>
              <span className="text-[11px] text-amber-700">
                1-Click instant test payment
              </span>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={processing}
              className="w-full bg-[#E8612C] hover:bg-[#D55320] text-white font-bold py-3 rounded-xl shadow-lg hover:shadow-xl transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <ShieldCheckIcon className="w-5 h-5" />
              Pay ₹{Number(price).toLocaleString('en-IN')} & Activate Plan
            </button>

            <p className="text-[11px] text-center text-gray-400">
              🔒 256-Bit SSL Encrypted • Cancel anytime from billing settings
            </p>
          </form>
        )}

      </div>
    </div>
  );
}
